import { useEffect, useRef, useState } from "react";

type DialogPosition = "bottom-start" | "top-start" | "right";

type DialogProps = {
  open: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  className?: string;
  position?: DialogPosition;
  containerClassName?: string;
};

export function Dialog({
  open,
  onClose,
  triggerRef,
  children,
  className = "",
  position = "top-start",
  containerClassName = "",
}: DialogProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [dialogPosition, setDialogPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        ref.current &&
        !ref.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose, triggerRef]);

  useEffect(() => {
    if (open && triggerRef.current && ref.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dialogRect = ref.current.getBoundingClientRect();
      const dialogHeight = dialogRect.height || 200; // Fallback height

      let top = 0;
      let left = 0;

      if (isMobile) {
        // On mobile, position relative to viewport
        switch (position) {
          case "bottom-start":
            top = rect.bottom + 8;
            left = rect.left;
            break;
          case "top-start":
            // Position dialog above the button
            top = rect.top - dialogHeight - 8;
            left = rect.left;
            break;
          case "right":
            top = rect.top;
            left = rect.right + 8;
            break;
        }

        // Ensure dialog stays within viewport
        const dialogWidth = 256;

        if (left + dialogWidth > window.innerWidth) {
          left = window.innerWidth - dialogWidth - 16;
        }

        if (top + dialogHeight > window.innerHeight) {
          top = rect.top - dialogHeight - 8;
        }

        if (top < 0) {
          top = 16;
        }

        if (left < 0) {
          left = 16;
        }
      } else {
        // On desktop, use relative positioning
        switch (position) {
          case "bottom-start":
            top = rect.bottom + 8;
            left = rect.left;
            break;
          case "top-start":
            // Position dialog above the button
            top = rect.top - dialogHeight - 8;
            left = rect.left;
            break;
          case "right":
            top = rect.top;
            left = rect.right + 8;
            break;
        }
      }

      // Ensure we have valid coordinates
      if (isNaN(top) || isNaN(left)) {
        top = 100;
        left = 100;
      }

      // Final check to keep dialog on screen
      if (top < 0) {
        top = 16;
      }

      setDialogPosition({ top, left });
    }
  }, [open, position, triggerRef, isMobile]);

  if (!open) return null;

  // Always use fixed positioning for reliability
  return (
    <div
      ref={ref}
      className={`fixed w-64 bg-white border border-gray-200 shadow-lg rounded-lg z-[99999] ${className}`}
      style={{
        top: `${dialogPosition.top}px`,
        left: `${dialogPosition.left}px`,
        backgroundColor: "white",
        zIndex: 99999,
      }}
    >
      <div className="p-4">{children}</div>
    </div>
  );
}
